import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import type { RequestPasswordResetDTO } from '@shared/schemas/auth';
import { requestPasswordResetSchema } from '@shared/schemas/auth';
import { getBrowserUrl, requestPasswordReset } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { AnimatedBrandLogo } from '@/components/animated-brand-logo';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const inputAuthClass =
  'h-12 rounded-lg border border-zinc-200 bg-white px-4 text-[15px] text-zinc-900 shadow-xs transition-colors placeholder:text-zinc-400 hover:border-zinc-300 focus-visible:border-zinc-950 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-950 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:hover:border-zinc-700 dark:focus-visible:border-zinc-100 dark:focus-visible:ring-zinc-100';

const labelAuthClass = 'text-sm font-medium text-zinc-800 dark:text-zinc-200';

export function ForgotPasswordForm({
  className,
  ...props
}: React.ComponentProps<'form'>) {
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const form = useForm<RequestPasswordResetDTO>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(requestPasswordResetSchema),
  });

  async function handleSubmit(values: RequestPasswordResetDTO) {
    form.clearErrors('root');

    const result = await requestPasswordReset(values.email, getBrowserUrl('/auth/reset-password'));

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    setSentEmail(values.email);
    toast.success('Si cet email existe, un lien de réinitialisation a été envoyé.');
  }

  return (
    <form className={cn('flex flex-col gap-0', className)} onSubmit={form.handleSubmit(handleSubmit)} {...props}>
      <FieldGroup className="gap-5">
        {/* Header Branding & Title */}
        <div className="auth-reveal-stagger-1 flex flex-col items-center pb-2 text-center">
          <Link
            to="/"
            className="mb-6 inline-flex items-center justify-center transition-opacity hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/10 rounded-lg"
            aria-label="Inersia - Accueil"
          >
            <AnimatedBrandLogo variant="hero" appearance="auto" className="h-10 w-auto sm:h-11 md:h-12" />
          </Link>
          <h1 className="text-balance text-2xl font-bold tracking-tight text-zinc-950 sm:text-[1.75rem] dark:text-zinc-50">
            Mot de passe oublié
          </h1>
          <p className="mt-2 text-balance text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
            Saisissez votre adresse e-mail pour récupérer l’accès à Inersia.
          </p>
        </div>

        <Field className="auth-reveal-stagger-2 gap-2">
          <FieldLabel htmlFor="forgot-email" className={labelAuthClass}>Email</FieldLabel>
          <Input
            id="forgot-email"
            data-testid="forgot-password-email"
            type="email"
            autoComplete="email"
            placeholder="anne.onyme@example.com"
            className={inputAuthClass}
            aria-invalid={form.formState.errors.email ? 'true' : 'false'}
            {...form.register('email')}
          />
          <FieldError errors={[form.formState.errors.email]} />
        </Field>

        <FieldError errors={[form.formState.errors.root]} />

        <Field className="auth-reveal-stagger-3 pt-2">
          <Button
            data-testid="forgot-password-submit"
            type="submit"
            size="lg"
            className="h-12 w-full rounded-lg bg-zinc-950 text-[15px] font-semibold text-white shadow-xs transition-all duration-150 hover:bg-zinc-800 active:scale-[0.99] dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? <Loader2Icon className="size-4.5 animate-spin" /> : null}
            Envoyer le lien
          </Button>
        </Field>

        <div className="auth-reveal-stagger-4 pt-2 text-center">
          <Link
            to="/auth/login"
            className="text-sm font-normal text-zinc-500 hover:text-zinc-900 transition-colors dark:text-zinc-400 dark:hover:text-zinc-100"
          >
            Retour à la connexion
          </Link>
        </div>

        {sentEmail ? (
          <FieldDescription data-testid="forgot-password-feedback" className="auth-reveal-stagger-5 pt-1 text-center text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
            Si un compte existe pour {sentEmail}, l'email de réinitialisation est en route.
          </FieldDescription>
        ) : null}
      </FieldGroup>
    </form>
  );
}
